import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import WorkspaceAPI from "../../api/WorkspaceAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  const { wPermissions } = useWorkspacePermissions();
  const tenantID = useAtomValue(tenantIDAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);

  const updateWorkspace = useMutation({
    mutationFn: ({
      payload,
      onSuccess,
    }: {
      payload: any;
      onSuccess?: () => void;
    }) => {
      if (!wPermissions.workspace_modify_workspace) {
        return Promise.reject(
          "User does not have permission to modify this workspace"
        );
      }

      return WorkspaceAPI.updateWorkspace(workspaceID, payload).then((data) => {
        const updatedWorkspace = data.data.data.workspace;
        return { updatedWorkspace, onSuccess };
      });
    },

    onSuccess: ({ updatedWorkspace, onSuccess }) => {
      if (workspaceID === updatedWorkspace.id) {
        queryClient.setQueryData<any>(
          ["workspace", workspaceID],
          (data: any) => ({
            ...data,
            workspace: updatedWorkspace,
          })
        );
      }

      queryClient.invalidateQueries({
        queryKey: ["workspaces", tenantID],
      });
      callFunctionIfExists(onSuccess);
    },
  });

  return {
    updateWorkspace: updateWorkspace.mutate,
    updateSubmitting: updateWorkspace.isPending,
    updateError: updateWorkspace.error
      ? getErrorMessage(updateWorkspace.error)
      : null,
  };
};

export default useUpdateWorkspace;
